import { t } from 'i18next';
import { showLicenseHintIfRequired } from '../../components/LicenseHint/LicenseHint';
import type { SolverSpeed, SolverTask } from '../../stores/SolverStore';
import { SolverEngine } from '../../types/planung-graphql-client-defs';
import { apiHelper } from '../apiHelper';
import { getTokens } from '../authStore';
import { showErrorToast } from '../toast';
import { ControlResult, GenericWarning } from './types';

const backendBaseUrl = () => import.meta.env.VITE_APP_BACKEND_URI + import.meta.env.VITE_APP_BACKEND_REST_PATH;

type TimetableVersionSolveResult = {
  jobId: string;
  status: string;
  gap: number;
  solutionFound: boolean;
  engine: SolverEngine;
  scoreDetails?: string | null;
};

type APIResponse<T> = {
  error?: string | null;
  data?: T;
};

export const testTimetableVersion = async (
  versionUuid: string,
  classes?: string[] | string,
): Promise<GenericWarning[]> => {
  return await apiHelper({
    path: `/test/timetableversion/${versionUuid}/${classes?.toString()}`,
    method: 'GET',
    type: 'json',
  });
};

export const controlTimetableVersion = async (
  versionUuid: string,
  classes?: string[] | string,
): Promise<ControlResult | undefined> => {
  return await apiHelper({
    path: `/control/timetable/${versionUuid}/${classes?.toString()}`,
    method: 'GET',
    type: 'json',
  });
};

const DEFAULT_ANALYZE_FETCH_POLICY = 'FETCH_ALL';

export const analyzeTimefoldTimetableVersion = async (
  versionUuid: string,
  options: {
    classes: string[];
    includeRoomResources: boolean;
    keepExistingPositions: boolean;
    includeSubjectContainer: boolean;
  },
  fetchPolicy: 'FETCH_ALL' | 'FETCH_SHALLOW' | 'FETCH_MATCH_COUNT' = DEFAULT_ANALYZE_FETCH_POLICY,
): Promise<unknown> => {
  const q = new URLSearchParams({ fetchPolicy });
  return await apiHelper({
    method: 'POST',
    path: `/../v2/solver/timefold/analyze/${versionUuid}?${q}`,
    data: JSON.stringify(options),
    type: 'json',
  });
};

export const solveTimetableVersionWithParams = async (
  versionUuid: string,
  options: {
    engine: SolverEngine;
    task: SolverTask;
    classes: string[];
    selectedClasses: string[];
    allowMoveOtherClasses: boolean;
    includeRoomResources: boolean;
    keepExistingPositions: boolean;
    includeSubjectContainer: boolean;
    speed: SolverSpeed;
  },
): Promise<APIResponse<TimetableVersionSolveResult> | undefined> => {
  const { engine, ...body } = options;
  const path =
    engine === SolverEngine.Timefold
      ? `/../v2/solver/timefold/solve/${versionUuid}`
      : `/../v2/solver/mathplan/solve/${versionUuid}`;

  return apiHelper({
    method: 'POST',
    path,
    data: JSON.stringify(body),
    type: 'json',
  })
    .then((res) => {
      return { data: res as TimetableVersionSolveResult | undefined };
    })
    .catch((err) => {
      showErrorToast({ name: t('pinboard.generate.error'), message: err.cause ?? err.message, graphQLErrors: [] });
      return { error: err.message };
    });
};

export const cancelTimetableVersionGeneration = async (jobId: string) => {
  return await apiHelper({
    path: `/../v2/solver/cancel/${jobId}`,
    method: 'POST',
    data: '',
    type: 'json',
  });
};

export const importTimetableVersionGeneration = async (jobId: string) => {
  return await apiHelper({
    path: `/../v2/solver/import/${jobId}`,
    method: 'POST',
    data: '',
    type: 'json',
  });
};

/** Stop the running solver early and import its current best solution. */
export const finishTimetableVersionGeneration = async (jobId: string) => {
  return await apiHelper({
    path: `/../v2/solver/finish/${jobId}`,
    method: 'POST',
    data: '',
    type: 'json',
  });
};

export const restartTimetableVersionMonitor = async (jobId: string) => {
  return await apiHelper({
    path: `/../v2/solver/monitor/${jobId}`,
    method: 'POST',
    data: '',
    type: 'json',
  });
};

export const triggerSolverCleanup = async () => {
  return await apiHelper({
    path: `/../v2/solver/cleanup`,
    method: 'POST',
    data: '',
    type: 'json',
  });
};

/**
 * aSc-XML der Version herunterladen. Reiner Export: das Backend legt dafür keinen Solver-Job an,
 * der Weg zurück ist der aSc-Import ({@link uploadAscTimetableImport}).
 */
export async function downloadAscExport(versionUuid: string, classes: string[]): Promise<boolean> {
  const tokens = getTokens();
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (tokens) {
    headers['Authorization'] = `Bearer ${tokens.access_token}`;
  }
  const url = `${backendBaseUrl()}/../v2/solver/asc/export/${versionUuid}`;
  try {
    const response = await fetch(url, { method: 'POST', body: JSON.stringify({ classes }), headers });
    if (!response.ok) {
      if (await showLicenseHintIfRequired(response)) return false;
      const err = await response.text();
      showErrorToast({ name: t('pinboard.actions.ascExport'), message: err, graphQLErrors: [] });
      return false;
    }
    const blob = await response.blob();
    const contentDisposition = response.headers.get('Content-Disposition');
    const match = contentDisposition?.match(/filename="?([^"]+)"?/);
    const fileName = match?.[1] ?? `timetable-asc-${versionUuid}.xml`;
    const blobURL = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = blobURL;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(blobURL);
    document.body.removeChild(a);
    return true;
  } catch (err: unknown) {
    showErrorToast({
      name: t('pinboard.actions.ascExport'),
      message: err instanceof Error ? err.message : String(err),
      graphQLErrors: [],
    });
    return false;
  }
}

/**
 * Modus des ASC-Imports: „keep" ergänzt die Version nur, „fresh" entfernt danach alles,
 * was die hochgeladene Datei nicht enthält.
 */
export type AscImportMode = 'keep' | 'fresh';

/** Art einer Entität, die der Import nicht auflösen konnte. */
export type AscUnresolvedKind = 'SUBJECT' | 'TEACHER' | 'CLASS' | 'DIVISION' | 'GROUP' | 'ROOM' | 'PERIOD' | 'LESSON';

export type AscUnresolvedEntity = {
  kind: AscUnresolvedKind;
  ascId: string;
  label: string;
  /**
   * `MAPPED` = der Anwender hat den Eintrag manuell zugeordnet (bzw. bewusst übersprungen). Er ist
   * kein offenes Problem, sondern wird nur zurückgemeldet, damit die getroffene Zuordnung im
   * Assistenten sichtbar bleibt und geändert werden kann; `targetUuid` trägt die gewählte UUID.
   */
  reason: 'NOT_FOUND' | 'AMBIGUOUS' | 'MAPPED';
  /** Rohwerte aus der Datei — für Anzeige und Vorauswahl im Dropdown. */
  name?: string;
  short?: string;
  grade?: number;
  /** Nur bei GROUP: schränkt die Auswahl auf die Gruppen dieser Klasse ein. */
  classUuid?: string;
  /**
   * Nur bei GROUP: Klasse + `divisiontag` aus der Datei. Gleiche `ascDivisionId` = in aSc dieselbe
   * Teilung, die Gruppen gehören also zusammen und müssen alle in DIESELBE Teilung zeigen — das
   * Backend verwirft eine Zuordnung, die in eine andere Teilung als die Geschwister zeigt.
   */
  ascDivisionId?: string;
  /**
   * Nur bei GROUP: die Teilung(en), auf die die ASC-Teilung schon festgelegt ist — durch eine
   * zugeordnete Teilung oder durch Geschwister-Gruppen, die das Backend bereits auflösen konnte.
   * Nicht leer ⇒ nur Gruppen dieser Teilung zur Auswahl stellen; leer ⇒ die erste Zuordnung des
   * Anwenders entscheidet über die Teilung.
   */
  divisionUuids?: string[];
  /**
   * Nur bei GROUP: Gruppen, die andere Zeilen derselben ASC-Teilung schon belegen — zwei parallel
   * unterrichtete ASC-Gruppen können nicht dieselbe Gruppe bei uns sein. Die eigene Zuordnung
   * (`targetUuid`) steht bewusst nicht darin.
   */
  takenGroupUuids?: string[];
  /** Bei AMBIGUOUS die in Frage kommenden Treffer. */
  candidateUuids?: string[];
  /** Nur bei `reason: 'MAPPED'`: die vom Anwender gewählte Ziel-UUID (`null` = übersprungen). */
  targetUuid?: string | null;
};

/** Arten, die der Anwender selbst zuordnen kann. */
export type AscMappingKind = 'SUBJECT' | 'TEACHER' | 'CLASS' | 'ROOM' | 'GROUP';

/** `targetUuid: null` = bewusst überspringen. */
export type AscManualMapping = {
  kind: AscMappingKind;
  ascId: string;
  targetUuid: string | null;
};

/** Was der Backend-Import gemacht hat — inkl. der Entitäten, die er nicht auflösen konnte. */
export type AscImportReport = {
  mode: 'KEEP' | 'FRESH';
  cardsInFile: number;
  cardsImported: number;
  cardsSkipped: number;
  createdLessons: number;
  createdCards: number;
  createdDivisions: number;
  createdGroups: number;
  linkedEntities: number;
  removedCards: number;
  removedLessons: number;
  disconnectedLessons: number;
  unresolved: AscUnresolvedEntity[];
  warnings: string[];
};

/**
 * aSc-Timetables-XML gegen eine Stundenplan-Version abgleichen: liest die Datei direkt gegen
 * die aktuelle Version ab — ohne Export und ohne Solver-Job.
 *
 * `preview: true` ist ein Trockenlauf: dieselbe Zuordnung, aber ohne jeden Schreibzugriff. Die
 * Zahlen im Report sagen dann, was der Import tun WÜRDE. Das FE zeigt erst diesen Report und
 * importiert nur nach Bestätigung.
 */
export async function uploadAscTimetableImport(
  versionUuid: string,
  file: File,
  mode: AscImportMode,
  options: { preview?: boolean; mappings?: AscManualMapping[] } = {},
): Promise<AscImportReport | undefined> {
  const tokens = getTokens();
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (tokens) {
    headers['Authorization'] = `Bearer ${tokens.access_token}`;
  }
  const path = options.preview ? `/import/asc/${versionUuid}/preview` : `/import/asc/${versionUuid}`;
  const url = `${backendBaseUrl()}${path}?mode=${mode}`;
  try {
    const body = JSON.stringify({ xml: await file.text(), mappings: options.mappings ?? [] });
    const response = await fetch(url, { method: 'POST', body, headers });
    if (!response.ok) {
      if (await showLicenseHintIfRequired(response)) return undefined;
      const errText = await response.text();
      showErrorToast({ name: t('pinboard.actions.ascImport'), message: errText, graphQLErrors: [] });
      return undefined;
    }
    return (await response.json()) as AscImportReport;
  } catch (err: unknown) {
    showErrorToast({
      name: t('pinboard.actions.ascImport'),
      message: err instanceof Error ? err.message : String(err),
      graphQLErrors: [],
    });
    return undefined;
  }
}
